// Dialogo para vincular un usuario de TRS Rastreo a uno o mas grupos
// (cuadrillas) YA EXISTENTES — analogo a TrsAsignarVehiculoDialog pero para
// grupos. Recibe la lista de grupos ya cargada por el padre (ClientePlataformaPanel
// ya la trae completa para armar el arbol, asi que no hace falta pedirla de
// nuevo ni paginarla — a diferencia de los vehiculos, la cantidad de grupos
// por cliente suele ser chica).
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { editarGrupo } from "../services/trsRastreoService";

export default function TrsAsignarGrupoDialog({ open, usuario, grupos = [], onClose, onSaved }) {
  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => {
    if (!open) return;
    setFilter("");
    setSelectedIds([]);
    setError("");
  }, [open]);

  const yaVinculadoIds = useMemo(
    () => new Set((grupos || [])
      .filter((g) => (g.usuarios_vinculados || []).some((u) => String(u.usuario_id) === String(usuario?.id)))
      .map((g) => String(g.id))),
    [grupos, usuario]
  );

  const f = filter.trim().toLowerCase();
  const filtrados = f
    ? grupos.filter((g) => String(g.cua_descripcion || "").toLowerCase().includes(f))
    : grupos;

  const selectedSet = new Set(selectedIds.map(String));
  const toggle = (id) => {
    const value = String(id);
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSelectedIds([...next]);
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!selectedIds.length) {
      const msg = "Selecciona al menos un grupo.";
      setError(msg);
      setSnack({ open: true, msg });
      return;
    }
    setSaving(true);
    setError("");
    try {
      await Promise.all(
        selectedIds.map((grupoId) =>
          editarGrupo(grupoId, { usuarios_agregar: [Number(usuario.id)] })
        )
      );
      // Se espera a que el padre termine de recargar el arbol antes de cerrar
      // (setSaving sigue en true, spinner visible) — si no, el dialogo se cierra
      // antes de que el cambio se refleje y el usuario ve el arbol viejo un rato.
      await onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo asignar el/los grupo(s).";
      setError(msg);
      setSnack({ open: true, msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>
          Asignar grupo a {usuario?.usu_username || usuario?.usu_nombre || "usuario"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1, overflowY: "visible" }}>
          {!!error && <Alert severity="error">{error}</Alert>}
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar grupo..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            disabled={saving}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Grupos</Typography>
            <Chip size="small" label={`${selectedSet.size} seleccionado(s)`} sx={{ height: 20, fontSize: "0.68rem" }} />
          </Box>
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, height: 320, overflowY: "auto" }}>
            {filtrados.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5, fontStyle: "italic" }}>
                Sin grupos para mostrar.
              </Typography>
            ) : (
              filtrados.map((g) => {
                const seleccionable = !yaVinculadoIds.has(String(g.id));
                const checked = selectedSet.has(String(g.id));
                const cantidadUnidades = (g.cuadrilla_relacion || []).filter((r) => r.cuarel_activo).length;
                return (
                  <Box
                    key={g.id}
                    onClick={() => seleccionable && toggle(g.id)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.4,
                      cursor: seleccionable ? "pointer" : "not-allowed",
                      opacity: seleccionable ? 1 : 0.45,
                      "&:hover": seleccionable ? { bgcolor: "action.hover" } : undefined,
                    }}
                  >
                    <Checkbox size="small" checked={checked} disabled={!seleccionable || saving} sx={{ p: 0.25 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: checked ? 900 : 600 }}>
                        {g.cua_descripcion || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {seleccionable ? `${cantidadUnidades} unidad(es)` : "Ya pertenece a este grupo"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
